import { useEffect, useMemo, useRef, useState } from 'react'
import { useSkin } from '../lib/skin'

/* ============================================================================
   Map.

   Tiles composed by hand rather than dropped in as an iframe or a library.

   The OpenStreetMap embed we had before arrived with OSM's own chrome bolted
   on — its zoom buttons, "Report a problem", "Make a Donation", a link to the
   API terms — over the default Mapnik style, which is a general-purpose map
   built to show everything at once. Yellow motorways and dense labels next to
   Encore's type read as somebody else's website in a hole in the page.

   So: Carto's basemaps, which are designed to sit under an interface rather
   than be one. Positron on Daylight, Dark Matter on Showtime, so the map
   changes with the skin instead of punching a bright rectangle through a dark
   screen. No key, no billing, no library — just Web Mercator arithmetic and a
   grid of images.
   ============================================================================ */

const TILE = 256
const SUBDOMAINS = ['a', 'b', 'c', 'd']

/** Required by Carto and by OSM's licence wherever these tiles appear. */
const ATTRIBUTION = '© OpenStreetMap · © CARTO'

interface Point {
  x: number
  y: number
}

/** Longitude/latitude → fractional world tile coordinates at a zoom level. */
function project(lat: number, lon: number, zoom: number): Point {
  const n = 2 ** zoom
  const radians = (Math.max(-85.05, Math.min(85.05, lat)) * Math.PI) / 180
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2) * n,
  }
}

export default function Map({
  lat,
  lon,
  label,
  height = 260,
  zoom: initialZoom = 14,
  interactive = true,
}: {
  lat: number
  lon: number
  label: string
  height?: number
  zoom?: number
  /** Off for print and for thumbnails, where a draggable map is a liability. */
  interactive?: boolean
}) {
  const { skin } = useSkin()
  const frame = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(640)
  const [zoom, setZoom] = useState(initialZoom)
  // Panning offset in pixels from the centred position. Reset when the pin
  // changes, so opening a different show doesn't inherit the last drag.
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 })

  useEffect(() => {
    setPan({ x: 0, y: 0 })
    setZoom(initialZoom)
  }, [lat, lon, initialZoom])

  // The tile grid is computed from the rendered width, so it has to be
  // measured rather than assumed.
  useEffect(() => {
    const element = frame.current
    if (!element) return
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width
      if (next) setWidth(next)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const retina = typeof window !== 'undefined' && window.devicePixelRatio > 1
  const style = skin === 'daylight' ? 'light_all' : 'dark_all'

  const tiles = useMemo(() => {
    const centre = project(lat, lon, zoom)
    // Pixel of the viewport's top-left corner in world space.
    const originX = centre.x * TILE - width / 2 - pan.x
    const originY = centre.y * TILE - height / 2 - pan.y

    const firstCol = Math.floor(originX / TILE)
    const firstRow = Math.floor(originY / TILE)
    const cols = Math.ceil(width / TILE) + 1
    const rows = Math.ceil(height / TILE) + 1
    const max = 2 ** zoom

    const out: Array<{ key: string; url: string; left: number; top: number }> = []

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const tileX = firstCol + col
        const tileY = firstRow + row
        // Vertical wrap has no meaning; horizontal does, so the world repeats.
        if (tileY < 0 || tileY >= max) continue
        const wrappedX = ((tileX % max) + max) % max

        out.push({
          key: `${zoom}/${tileX}/${tileY}`,
          url:
            `https://${SUBDOMAINS[(wrappedX + tileY) % SUBDOMAINS.length]}.basemaps.cartocdn.com/` +
            `${style}/${zoom}/${wrappedX}/${tileY}${retina ? '@2x' : ''}.png`,
          left: tileX * TILE - originX,
          top: tileY * TILE - originY,
        })
      }
    }
    return out
  }, [lat, lon, zoom, width, height, pan, style, retina])

  // ---- drag ----
  const dragging = useRef<{ x: number; y: number } | null>(null)

  function onPointerDown(event: React.PointerEvent) {
    if (!interactive) return
    dragging.current = { x: event.clientX, y: event.clientY }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  function onPointerMove(event: React.PointerEvent) {
    const from = dragging.current
    if (!from) return
    setPan((current) => ({
      x: current.x + (event.clientX - from.x),
      y: current.y + (event.clientY - from.y),
    }))
    dragging.current = { x: event.clientX, y: event.clientY }
  }
  function onPointerUp(event: React.PointerEvent) {
    dragging.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <div
      ref={frame}
      style={{
        position: 'relative',
        height,
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
        border: 'var(--hairline-width) solid var(--hairline)',
        background: 'var(--panel-raised)',
        cursor: interactive ? (dragging.current ? 'grabbing' : 'grab') : 'default',
        touchAction: 'none',
        userSelect: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="img"
      aria-label={`Map showing ${label}`}
    >
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          draggable={false}
          loading="lazy"
          style={{
            position: 'absolute',
            left: tile.left,
            top: tile.top,
            width: TILE,
            height: TILE,
            // Tiles land on fractional pixels; without this the seams show as
            // hairlines across the map.
            outline: '0.5px solid transparent',
          }}
        />
      ))}

      {/* The pin. Signal orange, drawn rather than imaged, so it stays crisp
          and stays on brand. Sits at the centre plus whatever has been panned. */}
      <div
        style={{
          position: 'absolute',
          left: width / 2 + pan.x,
          top: height / 2 + pan.y,
          transform: 'translate(-50%, -100%)',
          pointerEvents: 'none',
        }}
      >
        <svg width="26" height="34" viewBox="0 0 26 34" aria-hidden>
          <path
            d="M13 33s11-12.2 11-20A11 11 0 1 0 2 13c0 7.8 11 20 11 20Z"
            fill="var(--accent)"
            stroke="#fff"
            strokeWidth="2"
          />
          <circle cx="13" cy="13" r="4" fill="#fff" />
        </svg>
      </div>

      {interactive && (
        <div
          className="stack"
          style={{ position: 'absolute', top: 10, right: 10, gap: 4 }}
        >
          <ZoomButton label="Zoom in" onClick={() => setZoom((z) => Math.min(18, z + 1))}>
            +
          </ZoomButton>
          <ZoomButton label="Zoom out" onClick={() => setZoom((z) => Math.max(3, z - 1))}>
            −
          </ZoomButton>
        </div>
      )}

      {/* Attribution is a licence requirement, so it is present — but set in
          Encore's own spec type rather than in the tile provider's banner. */}
      <span
        className="t-spec-caption"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '4px 8px',
          fontSize: '0.56rem',
          textAlign: 'right',
          color: 'var(--text-tertiary)',
          background:
            'linear-gradient(to top, rgb(var(--map-scrim) / 0.72), rgb(var(--map-scrim) / 0))',
          pointerEvents: 'none',
        }}
      >
        {ATTRIBUTION}
      </span>
    </div>
  )
}

function ZoomButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        width: 30,
        height: 30,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 8,
        border: 'var(--hairline-width) solid var(--hairline)',
        background: 'var(--panel)',
        color: 'var(--text-primary)',
        fontSize: '1rem',
        lineHeight: 1,
        cursor: 'pointer',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      {children}
    </button>
  )
}
